import { useEffect, useState } from "react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/DashboardShell";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Server, Copy } from "lucide-react";
import { toast } from "@/lib/toast";

/** Version this website build expects from the server. Bump together with APP_VERSION in backend/server.py. */
export const EXPECTED_BACKEND = "2026.10.19";

/** Reads /api/health once; used for the "server needs updating" banner. */
export function useBackendHealth() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/health").then((r) => alive && setH(r.data || {})).catch((e) => alive && setH({ error: e?.response?.status || "offline" }));
    return () => { alive = false; };
  }, []);
  const outdated = h && (h.error || !h.version || h.version < EXPECTED_BACKEND);
  return { health: h, outdated };
}

export function OutdatedBanner({ health, onOpen }) {
  return (
    <div className="mb-6 rounded-2xl border border-rose-300 bg-rose-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="backend-outdated">
      <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0"/>
      <div className="flex-1 text-[14px] text-rose-900">
        <b>The server is running an older version{health?.version ? ` (${health.version})` : ""}.</b> Photo uploads, new categories, Razorpay, cash/UPI/cheque payments and the full city list need the updated backend ({EXPECTED_BACKEND}). Run the update on the server, then refresh this page.
      </div>
      <button onClick={onOpen} className="btn-wine !py-2 !px-4 text-sm shrink-0">How to update</button>
    </div>
  );
}

const Row = ({ ok, warn, label, children }) => (
  <div className="flex items-start gap-3 py-3 border-b border-[color:var(--line)]/70 last:border-0">
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5"/> : warn ? <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"/> : <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5"/>}
    <div className="min-w-0 flex-1"><div className="font-semibold text-[15px]">{label}</div><div className="text-[13px] text-neutral-600 mt-0.5 break-words">{children}</div></div>
  </div>
);

const DEPLOY_CMD = "cd /var/www/dartmyvenue && sudo bash deploy/deploy.sh";

/** Super-admin checklist: is the server updated, can it store photos, are payments & email configured. */
export default function SystemStatus() {
  const [s, setS] = useState(null);
  const [health, setHealth] = useState(null);
  const [serve, setServe] = useState(null); // can the browser open an uploaded file?
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    const h = await api.get("/health").then((r) => r.data).catch((e) => ({ error: e?.response?.status || "offline" }));
    setHealth(h);
    const st = await api.get("/admin/system-status").then((r) => r.data).catch((e) => ({ error: e?.response?.status || "offline" }));
    setS(st);
    if (st?.selftest_url) {
      try {
        const r = await fetch(`${mediaUrl(st.selftest_url)}?t=${Date.now()}`, { cache: "no-store" });
        const txt = r.ok ? await r.text() : "";
        setServe(r.ok && txt.startsWith("ok") ? "ok" : `HTTP ${r.status}`);
      } catch { setServe("unreachable"); }
    } else setServe(null);
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const old = !health || health.error || !health.version || health.version < EXPECTED_BACKEND;
  const rz = s?.razorpay || {};
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div className="space-y-6" data-testid="system-status">
      <Panel title="System status" action={<button onClick={load} className="btn-ghost !py-2 !px-3.5 text-sm"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`}/> Check again</button>}>
        <Row ok={!old} label="Server version">
          {health?.error ? `The server didn't answer /api/health (${health.error}).` : <>Running <b>{health?.version || "an old version (no version number)"}</b> · this website expects <b>{EXPECTED_BACKEND}</b>.{old && " Update the backend and restart it."}</>}
        </Row>
        {!s?.error && s && <>
          <Row ok={s.mongo} label="Database">{s.mongo ? `Connected · ${s.counts?.listings ?? 0} listings (${s.counts?.pending ?? 0} waiting), ${s.counts?.users ?? 0} users, ${s.counts?.cities ?? 0} cities in ${s.counts?.states ?? 0} states` : "Can't reach MongoDB — check MONGO_URL in backend/.env"}</Row>
          <Row ok={s.uploads_writable} label="Photo & video storage (saving)">{s.uploads_writable ? <>Files are saved in <code>{s.uploads_dir}</code></> : <>The server can't write to <code>{s.uploads_dir}</code>: {s.uploads_error}. Run <code>sudo chown -R www-data:www-data {s.uploads_dir}</code></>}</Row>
          <Row ok={serve === "ok"} warn={serve === null} label="Photo & video storage (showing)">{serve === "ok" ? "Uploaded files open correctly in the browser." : serve ? <>Uploaded files can't be opened ({serve}). nginx must pass <code>/api/uploads/</code> to the backend (or alias it to the uploads folder) — see DEPLOYMENT.md §3.</> : "Not checked."}</Row>
          <Row ok={rz.enabled} warn={rz.key_id_set && !rz.key_secret_set} label="Razorpay online payments">
            {rz.enabled ? <>Active in <b>{rz.mode || "unknown"}</b> mode{rz.mode === "test" ? " — test keys take no real money; switch to rzp_live_ keys to go live." : "."}{!rz.webhook_secret_set && " Add RAZORPAY_WEBHOOK_SECRET for automatic confirmation if a buyer closes the window early."}</>
              : <>Not active: {!rz.key_id_set && "RAZORPAY_KEY_ID is missing. "}{!rz.key_secret_set && "RAZORPAY_KEY_SECRET is missing. "}Add both to <code>backend/.env</code> and restart the backend. Until then the Pay buttons stay off and nothing is charged.</>}
          </Row>
          <Row ok={s.smtp} warn={!s.smtp} label="Email (contact form & reminders)">{s.smtp ? "SMTP is configured." : "SMTP is not set — messages are still saved in Admin → CMS → Messages, but no emails go out."}</Row>
          <Row ok label="Plans & categories">{(s.pricing_categories || []).join(", ") || "—"}</Row>
        </>}
        {s?.error && <Row ok={false} label="Detailed checks">Not available on this server version ({String(s.error)}). Update the backend to see storage, payment and email checks here.</Row>}
      </Panel>

      <RazorpayKeys onSaved={load}/>

      <Panel title="Updating the server">
        <div className="space-y-3 text-[14px] text-[color:var(--ink-2)]">
          <p>Upload the new project to the server (same folder as before), then run:</p>
          <div className="flex items-center gap-2 rounded-xl bg-[color:var(--maroon-2)] text-[#FBF7F4] px-4 py-3 font-mono text-[13px] overflow-x-auto"><Server className="w-4 h-4 shrink-0 text-[color:var(--gold-2)]"/><span className="whitespace-nowrap flex-1">{DEPLOY_CMD}</span><button onClick={() => copy(DEPLOY_CMD)} className="p-1 rounded hover:bg-white/10" aria-label="Copy"><Copy className="w-4 h-4"/></button></div>
          <p>The script installs the Python packages, checks the code starts, restarts the backend, builds the website and reloads nginx. It stops with a clear message if anything fails. Full steps are in <b>DEPLOYMENT.md</b>.</p>
        </div>
      </Panel>
    </div>
  );
}

/** Super admin: paste the Razorpay keys here instead of editing backend/.env. They are checked with Razorpay before saving. */
function RazorpayKeys({ onSaved }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => api.get("/admin/payment-keys").then((r) => { setInfo(r.data); setF((x) => ({ ...x, key_id: r.data?.saved_key_id || r.data?.key_id || "" })); }).catch((e) => setInfo({ error: e?.response?.status || "offline" }));
  useEffect(() => { if (user?.role === "super_admin") load(); }, [user?.role]);
  if (user?.role !== "super_admin") return null;

  const save = async (e) => {
    e.preventDefault();
    setMsg(null); setBusy(true);
    try {
      const r = await api.put("/admin/payment-keys", { key_id: f.key_id.trim(), key_secret: f.key_secret.trim() || null, webhook_secret: f.webhook_secret.trim() || null });
      setMsg({ ok: true, text: `Keys checked with Razorpay and saved. Online payments are on (${r.data?.mode} mode).` });
      setF((x) => ({ ...x, key_secret: "", webhook_secret: "" }));
      toast.success("Razorpay keys saved");
      load(); onSaved?.();
    } catch (er) {
      const st = er?.response?.status;
      setMsg({ ok: false, text: st === 404 || st === 405 ? "This needs the updated server. Update the backend first." : formatApiErrorDetail(er?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Panel title="Razorpay keys">
      {info?.error ? (
        <p className="text-[14px] text-[color:var(--ink-2)]">Not available on this server version. Update the backend, then come back here.</p>
      ) : (
        <form onSubmit={save} noValidate className="space-y-4" data-testid="rzp-form">
          <p className="text-[14px] text-[color:var(--ink-2)] max-w-[70ch]">
            Copy both values from Razorpay Dashboard → Account &amp; Settings → API Keys. We check them with Razorpay before saving.
            {info?.env_overrides && " Keys in backend/.env are being used right now; keys saved here take over if you remove those."}
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block"><span className="label">Key ID</span><input value={f.key_id} onChange={(e) => setF({ ...f, key_id: e.target.value })} placeholder="rzp_live_XXXXXXXXXXXX" className="input font-mono text-[14px]" autoComplete="off" spellCheck={false} data-testid="rzp-key-id"/></label>
            <label className="block"><span className="label">Key Secret {info?.saved_secret_set && <span className="font-normal text-neutral-500">(saved — leave empty to keep)</span>}</span><input type="password" value={f.key_secret} onChange={(e) => setF({ ...f, key_secret: e.target.value })} placeholder={info?.saved_secret_set ? "••••••••••••" : "Paste the secret"} className="input font-mono text-[14px]" autoComplete="new-password" data-testid="rzp-key-secret"/></label>
            <label className="block md:col-span-2"><span className="label">Webhook secret <span className="font-normal text-neutral-500">(optional)</span></span><input type="password" value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} placeholder="Only if you added the webhook in Razorpay" className="input font-mono text-[14px]" autoComplete="new-password"/></label>
          </div>
          {msg && <p className={`text-[14px] ${msg.ok ? "text-emerald-700" : "text-rose-700"}`} role={msg.ok ? "status" : "alert"} data-testid="rzp-msg">{msg.text}</p>}
          <button disabled={busy} className="btn-wine !py-2.5" data-testid="rzp-save">{busy ? "Checking with Razorpay…" : "Check & save keys"}</button>
        </form>
      )}
    </Panel>
  );
}
